import { useState, useRef, useEffect, KeyboardEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Terminal, Cpu, Database, Lock, ArrowRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface ConsoleProps {
  onEasterEgg?: (type: string) => void;
  compact?: boolean;
}

// Unified commands for both mobile and desktop
const COMMANDS: Record<string, { response: string[]; type?: string }> = {
  help: {
    response: [
      '╔══════════════════════════════════╗',
      '║     LOWKEYX COMMAND CENTER       ║',
      '╠══════════════════════════════════╣',
      '║  help    - Comandos disponibles  ║',
      '║  scan    - Demo OSINT            ║',
      '║  whoami  - Ver identidad         ║',
      '║  status  - Estado del sistema    ║',
      '║  modules - Listar módulos        ║',
      '║  clear   - Limpiar terminal      ║',
      '║  matrix  - ???                   ║',
      '╠══════════════════════════════════╣',
      '║  ⚡ Terminal demo. Usa la        ║',
      '║     Terminal Avanzada para       ║',
      '║     herramientas OSINT reales.   ║',
      '╚══════════════════════════════════╝',
    ],
    type: 'show_cta',
  },
  scan: {
    response: [
      '> Initializing OSINT framework...',
      '[████████████████████] 100%',
      '',
      '┌─ Scan Results (Demo) ─────────┐',
      '│ DNS Records:     14 found     │',
      '│ Subdomains:      23 detected  │', 
      '│ SSL Status:      Valid        │',
      '│ IP Reputation:   92/100       │',
      '└───────────────────────────────┘',
      '',
      '[DEMO] Simulación completada.',
    ],
    type: 'show_cta',
  },
  whoami: {
    response: [
      '┌─ Identity ────────────────────┐',
      '│ User:     anonymous_ghost     │',
      '│ Session:  ' + Math.random().toString(36).substring(2, 10) + '         │',
      '│ Access:   Demo Mode           │',
      '│ Status:   Preview             │',
      '└───────────────────────────────┘',
    ],
  },
  status: {
    response: [
      '┌─ System Status ───────────────┐',
      '│ ● API:       Online (99.9%)   │',
      '│ ● Database:  Operational      │',
      '│ ● Engine:    Ready            │',
      '│ ● Response:  < 200ms          │',
      '└───────────────────────────────┘',
    ],
  },
  modules: {
    response: [
      '┌─ Módulos Disponibles ─────────┐',
      '│ [01] Domain Recon      FREE   │',
      '│ [02] IP Intelligence   FREE   │',
      '│ [03] Email Recon       PRO    │',
      '│ [04] Email Hunter      PRO    │',
      '│ [05] Username Search   PRO    │',
      '├───────────────────────────────┤',
      '│ + Hash, Base64, UUID, etc.    │',
      '└───────────────────────────────┘',
    ],
    type: 'show_cta',
  },
  matrix: {
    response: ['> Activating Matrix mode...'],
    type: 'matrix',
  },
  sudo: {
    response: ['> Nice try 😉'],
  },
  hack: {
    response: ['> Real hackers use LOWKEYX.'],
  },
  coffee: {
    response: [
      '    ( (',
      '     ) )',
      '   ........',
      '   |      |]',
      '   \\      /',
      '    `----\'',
      '> Here\'s your coffee!',
    ],
    type: 'easter_egg',
  },
  tools: {
    response: ['> Redirigiendo...'],
    type: 'redirect_tools',
  },
  console: {
    response: ['> Redirigiendo...'],
    type: 'redirect_console',
  },
  terminal: {
    response: ['> Redirigiendo...'],
    type: 'redirect_console',
  },
};

// OSINT commands that redirect to full terminal
const OSINT_COMMANDS = ['domain', 'ip', 'email', 'hunter', 'username', 'phone', 'related', 'hash', 'base64', 'hex', 'rot', 'uuid', 'password', 'token'];

export const InteractiveConsole = ({ onEasterEgg, compact = false }: ConsoleProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [lines, setLines] = useState<Array<{ text: string; type: 'input' | 'output' | 'success' | 'error' | 'cta' }>>([
    { text: '> LOWKEYX Terminal [Demo]', type: 'output' },
    { text: '> Escribe "help" para ver comandos', type: 'output' },
    { text: '', type: 'output' },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [cursorVisible, setCursorVisible] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cursor blink effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCursorVisible(v => !v);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [lines]);

  // Maintain focus after any interaction
  const focusInput = useCallback(() => {
    // Small delay to ensure DOM is ready
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const typeResponse = async (responses: string[], type?: string) => {
    setIsTyping(true);
    
    for (const response of responses) {
      await new Promise(resolve => setTimeout(resolve, 30 + Math.random() * 20));
      setLines(prev => [...prev, { 
        text: response, 
        type: response.includes('●') ? 'success' : 
              response.includes('DEMO') || response.includes('ERROR') ? 'error' : 'output' 
      }]);
    }
    
    setIsTyping(false);
    
    // Handle special command types
    if (type === 'show_cta') {
      setLines(prev => [...prev, { text: '__CTA_INLINE__', type: 'cta' }]);
    } else if (type === 'matrix' && onEasterEgg) {
      onEasterEgg('matrix');
    } else if (type === 'easter_egg' && onEasterEgg) {
      onEasterEgg('easter_egg');
    } else if (type === 'redirect_tools') {
      setTimeout(() => navigate('/arsenal'), 500);
    } else if (type === 'redirect_console') {
      setTimeout(() => navigate('/arsenal?tool=console_terminal'), 500);
    }
    
    // CRITICAL: Restore focus after response
    focusInput();
  };

  const handleCommand = (cmd: string) => {
    const trimmedCmd = cmd.trim().toLowerCase();
    const baseCmd = trimmedCmd.split(' ')[0];
    
    if (trimmedCmd) {
      setCommandHistory(prev => [...prev, trimmedCmd]);
      setHistoryIndex(-1);
    }
    
    setLines(prev => [...prev, { text: `$ ${cmd}`, type: 'input' }]);
    setInput('');

    if (trimmedCmd === 'clear') {
      setLines([
        { text: '> Terminal cleared', type: 'output' },
        { text: '', type: 'output' },
      ]);
      focusInput();
      return;
    }

    // Check if trying to use an OSINT command
    if (OSINT_COMMANDS.includes(baseCmd)) {
      setLines(prev => [...prev, 
        { text: `> "${baseCmd}" disponible en Terminal Avanzada.`, type: 'error' },
        { text: '', type: 'output' },
        { text: '__CTA_INLINE__', type: 'cta' },
      ]);
      focusInput();
      return;
    }

    const command = COMMANDS[trimmedCmd];
    if (command) {
      typeResponse(command.response, command.type);
    } else if (trimmedCmd) {
      typeResponse([`> Comando no encontrado: ${trimmedCmd}`, '> Escribe "help" para ver comandos']);
      setLines(prev => [...prev, { text: '__CTA_INLINE__', type: 'cta' }]);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Block only arrow left/right for cursor navigation (not spacebar - it should add spaces)
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      return;
    }
    
    if (e.key === 'Enter' && !isTyping) {
      handleCommand(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : historyIndex;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex] || '');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex] || '');
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    }
  };

  // Handle touch on mobile to focus input
  const handleContainerTouch = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    // Don't prevent default for buttons/links
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      return;
    }
    focusInput();
  }, [focusInput]);

  return (
    <div 
      className={cn(
        "console-container rounded-xl overflow-hidden shadow-2xl cursor-text",
        "border border-primary/30 bg-gradient-to-b from-[hsl(0,5%,7%)] to-[hsl(0,5%,4%)]"
      )}
      onClick={handleContainerTouch}
      onTouchStart={handleContainerTouch}
    >
      {/* Console Content (chrome provided by parent TerminalChrome) */}
      <div 
        ref={containerRef}
        className={cn(
          "overflow-y-auto overflow-x-auto font-mono select-text p-3",
          // Responsive font sizes
          "text-[11px] sm:text-xs md:text-sm",
          // Responsive height
          compact ? "h-36 sm:h-48" : "h-40 sm:h-52 lg:h-60"
        )}
      >
        {lines.map((line, i) => {
          // Render inline CTA button
          if (line.type === 'cta' && line.text === '__CTA_INLINE__') {
            return (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/arsenal?tool=console_terminal');
                }}
                className={cn(
                  "flex items-center gap-2 mt-2 rounded-md w-full sm:w-auto",
                  "bg-primary/10 hover:bg-primary/20 border border-primary/30",
                  "transition-all group px-2.5 py-1.5 text-[11px] sm:text-xs"
                )}
              >
                <Terminal className="w-3 h-3 text-primary flex-shrink-0" />
                <span className="font-medium text-foreground">Terminal Avanzada</span>
                <ArrowRight className="w-3 h-3 text-primary group-hover:translate-x-0.5 transition-transform ml-auto" />
              </button>
            );
          }
          
          return (
            <div 
              key={i} 
              className={cn(
                "whitespace-pre leading-relaxed",
                line.type === 'input' && "text-primary",
                line.type === 'output' && "text-muted-foreground",
                line.type === 'success' && "text-terminal",
                line.type === 'error' && "text-amber-400"
              )}
            >
              {line.text}
            </div>
          );
        })}
        
        {/* Input Line */}
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-terminal flex-shrink-0 text-[11px] sm:text-xs md:text-sm">$</span>
          <div className="flex-1 relative min-w-0 flex items-center">
            {/* Visible text with cursor */}
            <span className="text-foreground font-mono whitespace-pre text-[11px] sm:text-xs md:text-sm">
              {input}
            </span>
            {/* Blinking cursor */}
            {!isTyping && (
              <span 
                className={cn(
                  "inline-block w-1.5 sm:w-2 h-4 bg-primary ml-px transition-opacity duration-100",
                  cursorVisible ? "opacity-100" : "opacity-0"
                )}
              />
            )}
            {/* Hidden input - captures keyboard */}
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isTyping}
              className="absolute inset-0 w-full opacity-0 cursor-text"
              autoComplete="off"
              spellCheck="false"
              autoCapitalize="off"
              enterKeyHint="send"
              aria-label="Terminal command input"
            />
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      <div className="border-t border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 px-3 py-2">
        <button
          onClick={() => navigate('/arsenal?tool=console_terminal')}
          className={cn(
            "w-full flex items-center justify-between gap-2 rounded-lg p-2",
            "bg-primary/10 hover:bg-primary/20 border border-primary/30 hover:border-primary/50",
            "transition-all group"
          )}
        >
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Terminal className="w-3.5 h-3.5 text-primary" />
            </div>
            <div className="text-left min-w-0">
              <p className="font-medium text-foreground text-xs sm:text-sm truncate">
                Terminal Avanzada
              </p>
              <p className="text-muted-foreground text-[10px] sm:text-xs truncate">
                Herramientas OSINT reales
              </p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
        </button>
      </div>

      {/* Stats Bar */}
      <div className="px-3 py-1.5 border-t border-border/50 flex items-center justify-between text-[9px] sm:text-[10px] font-mono text-muted-foreground bg-muted/20">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Cpu className="w-2.5 h-2.5" />
            <span className="hidden xs:inline">CPU:</span>23%
          </span>
          <span className="flex items-center gap-1">
            <Database className="w-2.5 h-2.5" />
            <span className="hidden xs:inline">MEM:</span>1.2GB
          </span>
          <span className="hidden sm:flex items-center gap-1">
            <Lock className="w-2.5 h-2.5 text-terminal" />
            E2E
          </span>
        </div>
        <span>v1 demo</span>
      </div>
    </div>
  );
};

export default InteractiveConsole;
